// TypeScript types for Charts V2 API payload
// Generated from backend/applications/api/src/api/routers/charts/ and api/analytics/expression.py

import type { TimeBucketValue } from 'components/TimeBucketSelector/time-bucket-options'
import { FilterOption, FilterTypes } from 'features/filters/types'

/**
 * All possible Dimension values from the backend Dimension enum
 */
export enum Dimension {
  CLUSTER_GROUP = 'cluster_group',
  CLUSTER_TOPIC = 'cluster_topic',
  CLUSTER_UUID = 'cluster_uuid',
  EMBEDDING = 'embedding',
  POST = 'post',
  POST_AVG_SENTIMENT = 'post_avg_sentiment',
  POST_COUNTRY = 'post_country',
  POST_CREATED_AT = 'post_created_at',
  POST_CREATED_DT = 'post_created_dt',
  POST_CUSTOM = 'post_custom',
  POST_IS_RATING_ONLY = 'post_is_rating_only',
  POST_PROMOTION = 'post_promotion',
  POST_SCORE = 'post_score',
  POST_WORD_COUNT = 'post_word_count',
  POST_TEXT = 'post_text_id',
  POST_TEXT_CUSTOM = 'post_text_custom',
  POST_TEXT_SENTIMENT = 'post_text_sentiment',
  POST_TEXT_TEXT = 'post_text_text',
  POST_TEXT_TEXT_SEARCH = 'post_text_text_search',
  POST_TEXT_UUID = 'post_text_uuid',
  POST_TEXT_KEYWORDS = 'post_text_keywords',
  PRODUCT_BRAND = 'product_brand',
  PRODUCT_CUSTOM = 'product_custom',
  PRODUCT_GROUP = 'product_group',
  PRODUCT_HIERARCHY = 'product_hierarchy',
  PRODUCT_NAME = 'product_name',
  PRODUCT_UUID = 'product_uuid',
  SOURCE_COUNTRY = 'source_country',
  SOURCE_CUSTOM = 'source_custom',
  SOURCE_NAME = 'source_name',
  SOURCE_SYNDICATED_SOURCE = 'source_syndicated_source',
  SOURCE_PROJECT_ID = 'source_project_id',
  SOURCE_TITLE = 'source_title',
  THEME = 'theme',
  PARENT_THEME = 'parent_theme',
  POST_UUID = 'post_uuid',
  POST_TITLE = 'post_title',
  POST_USERNAME = 'post_username',
  POST_URL = 'post_url',
  POST_PROCESS_TIME = 'post_process_time',
  // dimensions unique to "word counter" projects (e.g. search terms)
  INTERVAL_VOLUME = 'interval_volume',
  INTERVAL_VOLUME_TYPE = 'interval_volume_type',
  INTERVAL = 'interval',
  SEARCH_TERM = 'search_term',
}

/**
 * All possible Function values from the backend Function enum
 */
export enum Function {
  // aggregation functions
  AVG = 'avg',
  COUNT = 'count',
  COUNT_DISTINCT = 'count_distinct',
  MAX = 'max',
  MIN = 'min',
  SUM = 'sum',
  STDDEV = 'stddev',
  FILTER = 'filter',

  // datetime buckets
  DT_BUCKET_DAY = 'dt_bucket_day',
  DT_BUCKET_WEEK = 'dt_bucket_week',
  DT_BUCKET_MONTH = 'dt_bucket_month',
  DT_BUCKET_QUARTER = 'dt_bucket_quarter',
  DT_BUCKET_YEAR = 'dt_bucket_year',

  // dt functions
  NOW = 'current_timestamp',
  INTERVAL = 'interval',

  // logical operators
  AND = 'and',
  NOT = 'not',
  OR = 'or',
  GT = 'gt',
  LT = 'lt',
  GTE = 'gte',
  LTE = 'lte',
  EQ = 'eq',
  IN = 'in',
  CASE = 'Case',

  // array ops
  UNNEST = 'unnest',
  ARRAY_CAT = 'array_cat',

  // arithmetic operators
  ADD = 'add',
  DIVIDE = 'divide',
  MULTIPLY = 'multiply',
  SUBTRACT = 'subtract',
  ROUND = 'round',

  // regexp operators
  IREGEXP = 'IREGEXP',
  REGEXP_MATCHES = 'REGEXP_MATCHES',
  REGEXP_REPLACE = 'REGEXP_REPLACE',

  // text search functions
  MATCH = 'MATCH',
  LIKE = 'like',

  // string functions
  CONCAT = 'concat',

  // nulls
  COALESCE = 'coalesce',
  IS_NULL = 'is_null',
  IS_NOT_NULL = 'is_not_null',

  // JSON
  JSON_INDEX = 'json_index',
  JSON_INDEX_AS_JSON = 'json_index_as_json',

  // type management
  CAST = 'cast',
  TO_TIMESTAMP = 'to_timestamp',

  // identity function: returns the underlying data with no transformation
  IDENTITY = 'identity',

  // range operators
  LOWER = 'lower',
  UPPER = 'upper',

  // vector search ops
  COSINE_DISTANCE = 'cosine_distance',
}

/**
 * Forward declaration for recursive type definition
 */
export interface Expression {
  fn: Function
  args: FunctionArg[]
}

/**
 * JSON Dimension for custom field queries
 * Represents fields like post_custom->>'some_field' with optional casting
 */
export interface JSONDimension {
  field:
    | Dimension.POST_CUSTOM
    | Dimension.POST_TEXT_CUSTOM
    | Dimension.SOURCE_CUSTOM
    | Dimension.PRODUCT_CUSTOM
  keys: string[]
  cast_type?: string | null
}

/**
 * Function arguments can be dimensions, expressions, or literal values
 * Matches backend FunctionArg type annotation
 */
export type FunctionArg =
  | Dimension
  | JSONDimension
  | Expression
  | string
  | boolean
  | number
  | FunctionArg[]
  | null

/**
 * Domain types for the identifier - can be Dimension, JSONDimension, or Expression
 */
export type DomainType = Dimension | JSONDimension | Expression

/**
 * Chart identifier structure from types_v2.Identifier
 */
export interface Identifier {
  // e.g. for volume + sentiment over time by brand: [COUNT(POST), AVG(SENTIMENT)]
  aggregated_values: Expression[]

  // Filter constraints to apply to the query
  constraints: Expression[]

  // e.g. for volume + sentiment over time by brand: [time_interval, PRODUCT_BRAND]
  domains: DomainType[]
}

/**
 * Chart response value structure from types_v2.ChartValue
 * Backend sends datetime objects as ISO strings, not Date objects
 */
export interface ChartValue {
  // category order reflects Identifier.domains
  // Note: Backend sends datetime as ISO strings (e.g. "2024-01-15T00:00:00")
  category: Array<string | number>

  // values order reflects Identifier.aggregated_values
  values: Array<number | null>
}

/**
 * Chart response structure from types_v2.Chart
 * Matches the backend Chart dataclass with camelCase field names
 * (Backend uses JsonFriendlyDataclass.to_dict(with_camel_case=True))
 */
export interface Chart {
  identifier: Identifier
  data: ChartValue[]

  // Optional fields for brand/product/item grouping (My Brands vs Competitor Brands)
  // Backend sends snake_case, not camelCase.
  // category_mapping is keyed by the grouped axis's wire Dimension column
  // string ("product_brand", "product_hierarchy", "cluster_topic"); each
  // axis carries its own { bucketLabel: [children] } so simultaneous
  // groupings (e.g. grouped Brand under grouped Theme) coexist without
  // overwriting each other.
  parent_data?: ChartValue[] | null
  category_mapping?: Record<string, Record<string, string[]>> | null
  // Client-only, set by the YogiQL adapter: the server cut the result at the
  // YogiQL row ceiling, so the page shows its "too many datapoints" state.
  overDatapointLimit?: boolean
}

// ============================================================================
// FRONTEND FILTER TYPES (matching your existing code)
// ============================================================================

export type OperatorType = 'is' | 'is not'

export type NumericOperatorType =
  | 'greater than'
  | 'greater than or equal to'
  | 'less than'
  | 'less than or equal to'
  | 'equal to'
  | 'not equal to'

// Canonical numeric-operator list — the single source NumericOperatorPopover
// and the in-popover Records/Sentences pane derive their options from.
export const NUMERIC_OPERATOR_OPTIONS: {
  value: NumericOperatorType
  label: string
}[] = [
  { value: 'greater than', label: 'Greater than' },
  { value: 'greater than or equal to', label: 'Greater than or equal to' },
  { value: 'less than', label: 'Less than' },
  { value: 'less than or equal to', label: 'Less than or equal to' },
  { value: 'equal to', label: 'Equal to' },
  { value: 'not equal to', label: 'Not equal to' },
]

export type NumericFilterFieldType =
  | 'record_count'
  | 'sentence_count'
  | 'sentiment_filter'

// Shared constant for all numeric filter fields
export const NUMERIC_FILTER_FIELDS = [
  'record_count',
  'sentence_count',
  'sentiment_filter',
] as const satisfies readonly NumericFilterFieldType[]

// Type guard to check if a string is a numeric filter field
export function isNumericFilterField(
  field: string,
): field is NumericFilterFieldType {
  return (NUMERIC_FILTER_FIELDS as readonly string[]).includes(field)
}

// Labels for numeric filter fields (used in UI)
export const NUMERIC_FILTER_LABELS: Record<NumericFilterFieldType, string> = {
  record_count: 'Records',
  sentence_count: 'Sentences',
  sentiment_filter: 'Average Sentiment',
}

export type SelectedFilterOption = {
  field: FilterFieldType | NumericFilterFieldType
  operator: OperatorType | NumericOperatorType
  values: string[]
  // Numeric filter properties
  numericValue?: number
  // Window filter properties (for score/rating filters)
  min?: number
  max?: number
  valueType?: number // 0=all, 1=reviews only, 2=ratings only
  /** The grouped bar a drill-down expanded into `values`; YogiQL sends the
   *  bucket itself. Client-only: `toQueryPanel` (every v2 wire panel) and
   *  `buildDrillDownPanel` (drawer links' stored hash) strip it. */
  groupBucket?: GroupBucket
}

export type GroupBucket = {
  field: 'brand_group' | 'product_group' | 'parent_theme'
  value: string
}

/** One field's checkbox selection drafted in the add-filter popover, handed
 *  over on Apply. Empty `values` means "remove this field's filter". */
export type FilterSelectionDraft = {
  field: string
  values: string[]
}

export type DateRange = {
  startDate: Date | null
  endDate: Date | null
}

// ============================================================================
// UNIFIED METRIC CONFIGURATION
// ============================================================================

/**
 * Unified metric configuration - single source of truth
 * Contains UI labels, backend expressions, and IDs for each metric
 */
export const METRICS = {
  RECORD_COUNT: {
    id: 'record_count' as const,
    label: 'Records',
    expression: { fn: Function.COUNT, args: [Dimension.POST] } as Expression,
  },
  SENTENCE_COUNT: {
    id: 'sentence_count' as const,
    label: 'Sentences',
    expression: {
      fn: Function.COUNT,
      args: [Dimension.POST_TEXT],
    } as Expression,
  },
  SENTIMENT: {
    id: 'sentiment' as const,
    label: 'Sentiment',
    expression: {
      fn: Function.AVG,
      args: [Dimension.POST_TEXT_SENTIMENT],
    } as Expression,
  },
  RATING: {
    id: 'rating' as const,
    label: 'Rating',
    expression: {
      fn: Function.AVG,
      args: [Dimension.POST_SCORE],
    } as Expression,
  },
  NET_PROMOTER_SCORE: {
    id: 'net_promoter_score' as const,
    label: 'Net Promoter Score',
    expression: {
      fn: Function.AVG,
      args: [Dimension.POST_SCORE],
    } as Expression,
  },
} as const

/**
 * Metric type derived from configuration keys
 */
export type MetricType = (typeof METRICS)[keyof typeof METRICS]['id']

/**
 * Known filter field types with support for custom fields
 * Provides autocomplete for known fields while allowing any custom field
 */
export type FilterFieldType =
  // Known predefined fields
  | 'product_brand'
  | 'theme'
  | 'source_name'
  | 'created_at'
  | 'post_sentiment'
  | 'post_score'
  | 'country' // Friendly country filter name
  | 'post_country'
  | 'post_word_count'
  | 'post_title'
  | 'post_username'
  | 'post_url'
  | 'product_name'
  | 'product_group'
  | 'product_hierarchy'
  | 'source_country'
  | 'source_title'
  | 'source_syndicated_source'
  | 'parent_theme'
  | 'cluster_topic'
  | 'cluster_group'
  // Plus any custom field (for customer-specific fields)
  | string

// ============================================================================
// UNIFIED BREAKDOWN CONFIGURATION
// ============================================================================

/**
 * Unified breakdown configuration - single source of truth
 * Contains UI labels and backend dimension mappings for each breakdown
 */
export const BREAKDOWNS = {
  BRAND: {
    id: 'brand' as const,
    label: 'Brand',
    model: 'Product' as const,
    getDimension: (useGrouping?: boolean): DomainType => ({
      fn: Function.IDENTITY,
      args: [useGrouping ? Dimension.PRODUCT_GROUP : Dimension.PRODUCT_BRAND],
    }),
  },
  PRODUCT: {
    id: 'product' as const,
    label: 'Product',
    model: 'Product' as const,
    getDimension: (): DomainType => ({
      fn: Function.IDENTITY,
      args: [Dimension.PRODUCT_HIERARCHY],
    }),
  },
  PROMOTIONAL: {
    id: 'promotional' as const,
    label: 'Promotional',
    model: 'Post' as const,
    getDimension: (): DomainType => ({
      fn: Function.COALESCE,
      args: [
        {
          fn: Function.CASE,
          args: [
            Dimension.POST_PROMOTION,
            [['True', 'Promotional']],
            Dimension.POST_PROMOTION,
          ],
        },
        'Not Promotional',
      ],
    }),
  },
  THEME: {
    id: 'theme' as const,
    label: 'Theme',
    model: 'Post' as const,
    getDimension: (useGrouping?: boolean): DomainType => {
      if (useGrouping) {
        return { fn: Function.IDENTITY, args: [Dimension.PARENT_THEME] }
      }
      return {
        fn: Function.COALESCE,
        args: [Dimension.CLUSTER_GROUP, Dimension.CLUSTER_TOPIC],
      }
    },
  },
  SOURCE_TYPE: {
    id: 'source_type' as const,
    label: 'Source Type',
    model: 'Post' as const,
    getDimension: (): DomainType => ({
      fn: Function.COALESCE,
      args: [Dimension.SOURCE_NAME, 'Syndicated'],
    }),
  },
  COUNTRY: {
    id: 'country' as const,
    label: 'Country',
    model: 'Post' as const,
    getDimension: (): DomainType => ({
      fn: Function.COALESCE,
      args: [Dimension.POST_COUNTRY, Dimension.SOURCE_COUNTRY],
    }),
  },
  DATASET: {
    id: 'dataset' as const,
    label: 'Dataset',
    model: 'Source' as const,
    getDimension: (): DomainType => ({
      fn: Function.IDENTITY,
      args: [Dimension.SOURCE_PROJECT_ID],
    }),
  },
  SENTIMENT: {
    id: 'sentiment' as const,
    label: 'Sentiment',
    model: 'Sentence' as const,
    getDimension: (): DomainType => ({
      fn: Function.IDENTITY,
      args: [Dimension.POST_TEXT_SENTIMENT],
    }),
  },
  RATING: {
    id: 'rating' as const,
    label: 'Rating',
    model: 'Post' as const,
    getDimension: (): DomainType => ({
      fn: Function.IDENTITY,
      args: [Dimension.POST_SCORE],
    }),
  },
} as const

/**
 * Breakdown option type derived from configuration IDs
 */
export type BreakdownOption = (typeof BREAKDOWNS)[keyof typeof BREAKDOWNS]['id']

/**
 * Custom field breakdown - represents a project-specific custom field that can be used as a breakdown
 */
export interface CustomBreakdown {
  /** Unique field identifier (e.g., "aerosol_size") */
  field: string
  /** Display label (e.g., "Aerosol Size (oz)") */
  alias: string
  /** Optional display label (alternative to alias) */
  label?: string
  /** The model this custom field belongs to: "product", "source", "post", or "post_text" */
  model: 'product' | 'source' | 'post' | 'post_text'
}

/**
 * Maps custom field model to the appropriate Dimension
 */
export function getCustomDimension(
  model: CustomBreakdown['model'],
):
  | Dimension.POST_CUSTOM
  | Dimension.SOURCE_CUSTOM
  | Dimension.PRODUCT_CUSTOM
  | Dimension.POST_TEXT_CUSTOM {
  switch (model) {
    case 'product':
      return Dimension.PRODUCT_CUSTOM
    case 'source':
      return Dimension.SOURCE_CUSTOM
    case 'post':
      return Dimension.POST_CUSTOM
    case 'post_text':
      return Dimension.POST_TEXT_CUSTOM
    default:
      return Dimension.POST_CUSTOM
  }
}

/**
 * Creates a domain expression for a custom field breakdown
 * Uses JSON_INDEX_AS_JSON to access the custom field within the JSON column
 * (matching backend convention in apply_custom_fields)
 */
export function mapCustomBreakdownToDimension(
  customBreakdown: CustomBreakdown,
): DomainType {
  return {
    fn: Function.JSON_INDEX_AS_JSON,
    args: [getCustomDimension(customBreakdown.model), customBreakdown.field],
  }
}

/**
 * Creates an IS_NOT_NULL constraint for a custom field
 * This ensures records with null values for the custom field are filtered out
 */
export function createCustomFieldNotNullConstraint(
  customBreakdown: CustomBreakdown,
): Expression {
  return {
    fn: Function.IS_NOT_NULL,
    args: [
      {
        fn: Function.JSON_INDEX_AS_JSON,
        args: [
          getCustomDimension(customBreakdown.model),
          customBreakdown.field,
        ],
      },
    ],
  }
}

/**
 * Available time bucket options for time series charts
 */
export type TimeBucketOption = TimeBucketValue

// ============================================================================
// METRIC HELPER FUNCTIONS
// ============================================================================

/**
 * Get metric config by ID
 */
export function getMetricById(
  id: MetricType,
): (typeof METRICS)[keyof typeof METRICS] | undefined {
  return Object.values(METRICS).find((m) => m.id === id)
}

/**
 * Get metric config by label
 */
export function getMetricByLabel(
  label: string,
): (typeof METRICS)[keyof typeof METRICS] | undefined {
  return Object.values(METRICS).find((m) => m.label === label)
}

/**
 * Maps frontend metric ID to backend Expression
 */
export function mapMetricToExpression(metric: MetricType): Expression {
  const config = getMetricById(metric)
  if (!config) {
    return { fn: Function.COUNT, args: [Dimension.POST] }
  }
  return config.expression
}

/**
 * Maps backend Expression to frontend metric ID (reverse lookup)
 */
export function mapExpressionToMetric(
  expression: Expression,
): MetricType | null {
  // Backend serializes Dimension enums as {"dimension": "value"} objects.
  // Normalize args to plain strings before comparing against METRICS definitions.
  const normalizeArg = (arg: unknown): string | unknown => {
    if (arg && typeof arg === 'object' && 'dimension' in arg) {
      return (arg as { dimension: string }).dimension
    }
    return arg
  }

  // Ask Yogi uses different dimension names than the regular chart pipeline.
  // Map them to their METRICS equivalents so the reverse lookup works.
  // Source of truth: backend/applications/api/src/api/routers/ask_yogi/chatbot_dimension.py
  // If backend dimension names change, update this map to match.
  const DIMENSION_ALIASES: Record<string, string> = {
    post_text_sentiment: Dimension.POST_TEXT_SENTIMENT, // ChatbotDimension.SENTIMENT
    post_text_text: Dimension.POST_TEXT, // ChatbotDimension.POST_TEXT (sentence counts)
  }

  const normalizeAndAlias = (arg: unknown): unknown => {
    const normalized = normalizeArg(arg)
    return typeof normalized === 'string'
      ? (DIMENSION_ALIASES[normalized] ?? normalized)
      : normalized
  }

  const found = Object.values(METRICS).find((m) => {
    const expr = m.expression
    return (
      expr.fn === expression.fn &&
      expr.args.length === expression.args.length &&
      expr.args[0] === normalizeAndAlias(expression.args[0])
    )
  })
  return found?.id || null
}

/**
 * Maps multiple metrics to expressions array
 */
export function mapMetricsToExpressions(metrics: MetricType[]): Expression[] {
  return metrics.map(mapMetricToExpression)
}

// ============================================================================
// BREAKDOWN HELPER FUNCTIONS
// ============================================================================

/**
 * Get breakdown config by ID
 */
export function getBreakdownById(
  id: BreakdownOption,
): (typeof BREAKDOWNS)[keyof typeof BREAKDOWNS] | undefined {
  return Object.values(BREAKDOWNS).find((b) => b.id === id)
}

/**
 * Get breakdown config by label (for backwards compatibility)
 */
export function getBreakdownByLabel(
  label: string,
): (typeof BREAKDOWNS)[keyof typeof BREAKDOWNS] | undefined {
  return Object.values(BREAKDOWNS).find((b) => b.label === label)
}

/**
 * Maps frontend breakdown ID to backend dimension expression
 */
export function mapBreakdownToDimension(
  breakdown: BreakdownOption,
  useGrouping?: boolean,
): DomainType {
  const config = getBreakdownById(breakdown)
  if (!config) {
    return { fn: Function.IDENTITY, args: [Dimension.PRODUCT_BRAND] }
  }
  return config.getDimension(useGrouping)
}

/**
 * Maps backend dimension back to frontend breakdown ID (reverse lookup)
 */
export function mapDimensionToBreakdown(
  dimension: Dimension,
): BreakdownOption | null {
  // Map single dimensions to breakdowns
  const dimensionMap: Record<string, BreakdownOption> = {
    [Dimension.PRODUCT_BRAND]: BREAKDOWNS.BRAND.id,
    [Dimension.PRODUCT_GROUP]: BREAKDOWNS.BRAND.id,
    [Dimension.PRODUCT_HIERARCHY]: BREAKDOWNS.PRODUCT.id,
    [Dimension.POST_PROMOTION]: BREAKDOWNS.PROMOTIONAL.id,
    [Dimension.PARENT_THEME]: BREAKDOWNS.THEME.id,
    [Dimension.CLUSTER_GROUP]: BREAKDOWNS.THEME.id,
    [Dimension.CLUSTER_TOPIC]: BREAKDOWNS.THEME.id,
    [Dimension.SOURCE_NAME]: BREAKDOWNS.SOURCE_TYPE.id,
    [Dimension.POST_COUNTRY]: BREAKDOWNS.COUNTRY.id,
    [Dimension.SOURCE_COUNTRY]: BREAKDOWNS.COUNTRY.id,
    [Dimension.SOURCE_PROJECT_ID]: BREAKDOWNS.DATASET.id,
    [Dimension.POST_TEXT_SENTIMENT]: BREAKDOWNS.SENTIMENT.id,
    [Dimension.POST_SCORE]: BREAKDOWNS.RATING.id,
    // TIME dimensions removed - time is no longer a breakdown, it's managed via selectedTimeBucket
  }

  const breakdown = dimensionMap[dimension]
  if (!breakdown) {
    return null
  }
  return breakdown
}

/**
 * Maps time bucket options to backend time bucketing expressions
 * Backend expects: {fn: 'dt_bucket_month', args: [dimension]}
 * The function name itself specifies the bucket type
 */
export function mapTimeBucketToDimension(
  timeBucket: TimeBucketOption,
): DomainType {
  switch (timeBucket) {
    case 'day':
      return { fn: Function.DT_BUCKET_DAY, args: [Dimension.POST_CREATED_AT] }

    case 'week':
      return { fn: Function.DT_BUCKET_WEEK, args: [Dimension.POST_CREATED_AT] }

    case 'month':
      return {
        fn: Function.DT_BUCKET_MONTH,
        args: [Dimension.POST_CREATED_AT],
      }

    case 'quarter':
      return {
        fn: Function.DT_BUCKET_QUARTER,
        args: [Dimension.POST_CREATED_AT],
      }

    case 'year':
      return { fn: Function.DT_BUCKET_YEAR, args: [Dimension.POST_CREATED_AT] }

    default:
      // Default to monthly bucketing
      return {
        fn: Function.DT_BUCKET_MONTH,
        args: [Dimension.POST_CREATED_AT],
      }
  }
}

// ============================================================================
// SERIALIZATION HELPERS
// ============================================================================

/**
 * Serialized function argument types for the backend
 */
export type SerializedFunctionArg =
  | { dimension: string }
  | { fn: string; args: SerializedFunctionArg[] }
  | { field: { dimension: string }; keys: string[]; cast_type?: string | null }
  | SerializedFunctionArg[]
  | string
  | boolean
  | number
  | null
  | undefined

/**
 * Recursively serializes FunctionArgs to wrap Dimension enums in {"dimension": "..."} format
 * This is required by the backend's deserialization logic.
 */
export function serializeFunctionArg(arg: FunctionArg): SerializedFunctionArg {
  if (arg === null || arg === undefined) return arg

  // Wrap Dimension enum values
  if (
    typeof arg === 'string' &&
    Object.values(Dimension).includes(arg as Dimension)
  ) {
    return { dimension: arg }
  }

  // Recursively serialize Expression objects
  if (typeof arg === 'object' && 'fn' in arg && 'args' in arg) {
    return {
      fn: arg.fn,
      args: arg.args.map(serializeFunctionArg),
    }
  }

  // Recursively serialize arrays
  if (Array.isArray(arg)) {
    return arg.map(serializeFunctionArg)
  }

  // Handle JSONDimension objects
  if (typeof arg === 'object' && 'field' in arg && 'keys' in arg) {
    return {
      ...arg,
      field: { dimension: arg.field },
    }
  }

  // Return primitives as-is
  return arg
}

/**
 * Serializes a domain (Dimension or Expression) for the backend
 */
export function serializeDomain(domain: DomainType): SerializedFunctionArg {
  if (
    typeof domain === 'string' &&
    Object.values(Dimension).includes(domain as Dimension)
  ) {
    return { dimension: domain }
  }

  if (typeof domain === 'object' && 'fn' in domain && 'args' in domain) {
    return {
      fn: domain.fn,
      args: domain.args.map(serializeFunctionArg),
    }
  }

  if (typeof domain === 'object' && 'field' in domain && 'keys' in domain) {
    return {
      ...domain,
      field: { dimension: domain.field },
    }
  }

  return domain as SerializedFunctionArg
}

/**
 * Maps frontend filter field names to backend Dimension enum values
 * Add mappings as needed based on your filter options
 */
export function mapFieldToDimension(
  field: FilterFieldType,
  filterOptions?: FilterOption[],
): Dimension | null {
  // Map from friendly field name to Dimension
  // NOTE: We check fieldMap FIRST because some fields need explicit mapping
  // (e.g., 'theme' -> CLUSTER_UUID, not THEME which maps to cluster_id Int32)
  const fieldMap: Record<string, Dimension> = {
    // Post fields
    post_country: Dimension.POST_COUNTRY,
    created_at: Dimension.POST_CREATED_AT,
    post_created_at: Dimension.POST_CREATED_AT,
    sentiment: Dimension.POST_AVG_SENTIMENT,
    post_sentiment: Dimension.POST_AVG_SENTIMENT,
    post_score: Dimension.POST_SCORE,
    post_word_count: Dimension.POST_WORD_COUNT,
    post_title: Dimension.POST_TITLE,
    post_username: Dimension.POST_USERNAME,
    post_url: Dimension.POST_URL,

    // Product fields
    brand: Dimension.PRODUCT_BRAND,
    product: Dimension.PRODUCT_NAME, // API returns 'product' field name
    product_name: Dimension.PRODUCT_NAME,
    product_group: Dimension.PRODUCT_GROUP,
    product_hierarchy: Dimension.PRODUCT_HIERARCHY,

    // Source fields
    source: Dimension.SOURCE_TITLE, // API returns 'source' field name
    source_type: Dimension.SOURCE_NAME, // API returns 'source_type' field name
    source_name: Dimension.SOURCE_NAME,
    source_country: Dimension.SOURCE_COUNTRY,
    source_title: Dimension.SOURCE_TITLE,
    source_syndicated_source: Dimension.SOURCE_SYNDICATED_SOURCE,

    // Theme fields
    theme: Dimension.CLUSTER_UUID, // Use cluster_uuid for filtering (THEME maps to cluster_id Int32)
    parent_theme: Dimension.PARENT_THEME,
    cluster_topic: Dimension.CLUSTER_TOPIC,
    cluster_group: Dimension.CLUSTER_GROUP,

    // Country filter (uses friendly name 'country' in UI, maps to POST_COUNTRY for filtering)
    country: Dimension.POST_COUNTRY,

    // Promotional filter
    promotion: Dimension.POST_PROMOTION,

    // Dataset/Project filter (uses cluster_uuid like theme)
    proj_uuid: Dimension.SOURCE_PROJECT_ID,

    // Add more mappings as needed based on your actual filter field names
  }

  // First try the explicit mapping
  if (fieldMap[field]) {
    return fieldMap[field]
  }

  // Check if this is a custom field by looking it up in filterOptions
  if (filterOptions) {
    const filterOption = filterOptions.find((opt) => opt.field === field)
    if (filterOption) {
      // If the filter has an explicit model property, use it
      if ('model' in filterOption && filterOption.model) {
        // Map the model name to the appropriate custom dimension
        const modelMapping: Record<string, Dimension> = {
          ProductModel: Dimension.PRODUCT_CUSTOM,
          SourceModel: Dimension.SOURCE_CUSTOM,
          PostModel: Dimension.POST_CUSTOM,
          PostTextModel: Dimension.POST_TEXT_CUSTOM,
        }
        const dimension = modelMapping[filterOption.model]
        if (dimension) {
          return dimension
        }
      }

      // If the filter exists but doesn't have a model property,
      // treat it as a product custom field (most dynamic attributes are product-level)
      // This handles product attributes like "form", "flavors", "container_type", etc.
      if (filterOption.filter_type === FilterTypes.SELECT) {
        return Dimension.PRODUCT_CUSTOM
      }
    }
  }

  // Fallback: if the field is already a valid Dimension enum value
  // (this happens when loading from URL hash where constraints use Dimensions directly)
  if (Object.values(Dimension).includes(field as Dimension)) {
    return field as Dimension
  }

  return null
}

/**
 * Maps a backend Dimension enum value back to the friendly field name used in the UI.
 * This is the reverse of mapFieldToDimension.
 */
export function mapDimensionToField(dimension: Dimension): string {
  const dimensionMap: Partial<Record<Dimension, string>> = {
    // Post fields
    [Dimension.POST_COUNTRY]: 'country', // Map to friendly 'country' name used in UI
    [Dimension.POST_CREATED_AT]: 'created_at',
    [Dimension.POST_AVG_SENTIMENT]: 'post_sentiment',
    [Dimension.POST_SCORE]: 'post_score',
    [Dimension.POST_WORD_COUNT]: 'post_word_count',
    [Dimension.POST_TITLE]: 'post_title',
    [Dimension.POST_USERNAME]: 'post_username',
    [Dimension.POST_URL]: 'post_url',
    [Dimension.POST]: 'post',
    [Dimension.SOURCE_PROJECT_ID]: 'proj_uuid', // Dataset filter
    [Dimension.POST_PROMOTION]: 'promotion',

    // Product fields
    [Dimension.PRODUCT_BRAND]: 'brand',
    [Dimension.PRODUCT_NAME]: 'product', // Backend uses 'product' field name
    [Dimension.PRODUCT_GROUP]: 'product_group',
    [Dimension.PRODUCT_HIERARCHY]: 'product_hierarchy',

    // Source fields
    [Dimension.SOURCE_NAME]: 'source_type', // Backend uses 'source_type' field name
    [Dimension.SOURCE_COUNTRY]: 'source_country',
    [Dimension.SOURCE_TITLE]: 'source', // Backend uses 'source' field name
    [Dimension.SOURCE_SYNDICATED_SOURCE]: 'source_syndicated_source',

    // Theme fields
    [Dimension.THEME]: 'theme',
    [Dimension.CLUSTER_UUID]: 'theme', // Maps cluster_uuid back to 'theme' for filter display
    [Dimension.PARENT_THEME]: 'parent_theme',
    [Dimension.CLUSTER_TOPIC]: 'cluster_topic',
    [Dimension.CLUSTER_GROUP]: 'cluster_group',
  }

  return dimensionMap[dimension] || dimension // Fallback to dimension itself if not mapped
}

// ============================================================================
// TYPE GUARDS & UTILITIES
// ============================================================================

/**
 * Type guard to check if a value is a Dimension
 */
export function isDimension(value: unknown): value is Dimension {
  return (
    typeof value === 'string' &&
    Object.values(Dimension).includes(value as Dimension)
  )
}

/**
 * Type guard to check if a value is a JSONDimension
 */
export function isJSONDimension(value: unknown): value is JSONDimension {
  return (
    typeof value === 'object' &&
    value !== null &&
    'field' in value &&
    'keys' in value &&
    Array.isArray((value as JSONDimension).keys)
  )
}

/**
 * Type guard to check if a value is an Expression
 */
export function isExpression(value: unknown): value is Expression {
  return (
    typeof value === 'object' &&
    value !== null &&
    'fn' in value &&
    'args' in value &&
    Array.isArray((value as Expression).args)
  )
}

/**
 * Get the model label for a filter field
 * Returns the capitalized model name (e.g., "Product", "Post", "Source")
 */
export function getFilterFieldModel(field: string): string | null {
  const fieldModelMap: Record<string, string> = {
    // Product fields
    brand: 'Product',
    product_brand: 'Product',
    product: 'Product',
    product_name: 'Product',
    product_group: 'Product',
    product_hierarchy: 'Product',
    // Post fields
    theme: 'Post',
    parent_theme: 'Post',
    country: 'Post',
    post_country: 'Post',
    promotion: 'Post',
    post_score: 'Post',
    post_title: 'Post',
    post_username: 'Post',
    post_url: 'Post',
    post_word_count: 'Post',
    post_sentiment: 'Post',
    post_promotion: 'Post',
    cluster_topic: 'Post',
    cluster_group: 'Post',
    proj_uuid: 'Source',
    // Source fields
    source: 'Source',
    source_type: 'Source',
    source_name: 'Source',
    source_country: 'Source',
    source_title: 'Source',
    source_syndicated_source: 'Source',
    // Sentence fields
    sentiment: 'Sentence',
  }
  return fieldModelMap[field] || null
}

/**
 * Helper to capitalize and clean model names from API
 * Converts "ProductModel" -> "Product", "PostModel" -> "Post", etc.
 */
export function capitalizeModel(model: string | undefined): string | null {
  if (!model) return null
  // Remove "Model" suffix if present
  const cleaned = model.replace(/Model$/, '')
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
}

/**
 * Flatten searchCriteria from Record format to flat array for API consumption
 * Converts { 'Includes all': ['kw1', 'kw2'], 'Excludes some': ['kw3'] } -> ['kw1', 'kw2', 'kw3']
 */
export function flattenSearchCriteria(
  searchCriteria: Record<string, string[]>,
): string[] {
  return Object.values(searchCriteria).flat()
}
